import React from 'react';
import { Model } from '../types/BookingTypes';

interface ModelSelectorProps {
  models: Model[];
  selectedModel: Model | null;
  onSelect: (model: Model) => void;
  searchQuery: string;
  onSearch: (query: string) => void;
  onBack?: () => void;
  brandName?: string;
}

const ModelSelector: React.FC<ModelSelectorProps> = ({ 
  models, 
  selectedModel, 
  onSelect, 
  searchQuery, 
  onSearch, 
  onBack, 
  brandName 
}) => {
  return (
    <div className="bg-white rounded-xl shadow-sm">
      {/* Sticky Header and Search Container */}
      <div className="sticky top-0 z-10 bg-white rounded-t-xl">
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-100 flex items-center">
          {onBack && (
            <button
              onClick={onBack}
              className="mr-3 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors"
            >
              <i className="fas fa-arrow-left text-gray-600"></i>
            </button>
          )}
          <h3 className="text-base font-medium text-gray-800">
            Select {brandName} Model
          </h3>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-100 bg-white">
          <div className="relative flex items-center">
            <i className="fas fa-search absolute left-3 text-gray-400 text-sm pointer-events-none"></i>
            <input
              type="text"
              placeholder="Search model..."
              value={searchQuery}
              onChange={e => onSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-gray-50 border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-minto-500/20 focus:border-minto-500"
            />
          </div>
        </div>
      </div>

      {/* Models Grid with padding to account for sticky header */}
      <div className="p-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {models.map((model) => (
            <button
              key={model.id}
              className={`relative flex flex-col items-center justify-center p-3 rounded-xl transition-all duration-200 ${
                selectedModel?.id === model.id 
                  ? 'bg-minto-50 border-2 border-minto-500' 
                  : 'bg-gray-50/50 border border-gray-100 hover:border-minto-200 hover:bg-minto-50/50 active:scale-95'
              }`}
              onClick={() => onSelect(model)}
            >
              <div className="w-16 h-16 mb-2 flex items-center justify-center">
                <img 
                  src={model.image} 
                  alt={model.name} 
                  className="w-full h-full object-contain" 
                />
              </div>
              <span className="text-xs font-medium text-center text-gray-700 line-clamp-2">{model.name}</span>
              {selectedModel?.id === model.id && (
                <div className="absolute top-2 right-2">
                  <i className="fas fa-check-circle text-minto-500 text-sm"></i>
                </div>
              )}
            </button>
          ))}
        </div>

        {models.length === 0 && (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-gray-100">
              <i className="fas fa-search text-gray-400 text-xl"></i>
            </div>
            <p className="text-gray-500 text-sm">No models found matching your search.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModelSelector; 